import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/react-hooks';
import Modal from '@mui/material/Modal';
import Rating from '@mui/material/Rating';

import './mobile-total-review-modal-style.scss';

import { Loading } from '@atoms';
import Typography from '@src/componentsNew/Typography/Typography';
import TopButton from '@src/componentsNew/Buttons/TopButton/TopButton';
import MobileCustomHeader from '@src/componentsNew/Header/CustomHeader/MobileCustomHeader';
import MobileReviewListItem from '@src/componentsNew/ListItem/ReviewListItem/MobileReviewListItem';
import MobileReviewSortingFilter from '@src/componentsNew/Modals/FilterModal/MobileReviewSortingFilter/MobileReviewSortingFilter';
import MobileReviewFilter from '@src/componentsNew/Modals/FilterModal/MobileReviewFilter/MobileReveiwFilter';

import StarIcon from '@src/componentsNew/Icons/Icons/StarIcon';
import SortListIcon from '@src/componentsNew/Icons/Icons/SortListIcon';
import FilterListIcon from '@src/componentsNew/Icons/Icons/FilterListIcon';

import { REVIEWS } from '@molecules/ReviewList/queries';
import { reviews, reviewsVariables } from '@molecules/ReviewList/__generated__/reviews';

interface IProps {
  open: boolean;
  onClose: () => void;
  initialVariables?: reviewsVariables;
}

const MobileTotalReviewModal = ({ open, onClose, initialVariables }: IProps) => {
  const [variables, setVariables] = useState<reviewsVariables>(initialVariables);
  const [isSortingFilterModalOpen, setIsSortingFilterModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isShowTopBtn, setIsShowTopBtn] = useState(false);

  const fetchingRef = useRef(false);
  const hasNextPageRef = useRef(false);
  const modalContentWrapperRef = useRef(null);
  const secondLastItemRef = useRef(null);

  const { data, loading, fetchMore, refetch } = useQuery<reviews>(REVIEWS, {
    variables,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      const reviews = data.reviews;

      hasNextPageRef.current = reviews?.pageInfo?.hasNextPage;
    },
  });

  useEffect(() => {
    const handleScroll = () => {
      if (modalContentWrapperRef.current) {
        const scrollTop = modalContentWrapperRef.current.scrollTop;

        setIsShowTopBtn(scrollTop > 0);
      }
    };

    if (modalContentWrapperRef.current) {
      modalContentWrapperRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (modalContentWrapperRef.current) {
        modalContentWrapperRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [data]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPageRef.current && !fetchingRef.current) {
        fetchingRef.current = true;

        const newVariables = {
          ...variables,
          after: data.reviews.pageInfo.endCursor,
        };

        fetchMore({
          variables: newVariables,
          updateQuery: (prevResult: reviews, { fetchMoreResult }) => {
            const reviews = fetchMoreResult?.reviews;
            const newEdges = reviews?.edges;
            const pageInfo = reviews?.pageInfo;
            const totalCount = reviews?.totalCount;

            hasNextPageRef.current = pageInfo?.hasNextPage;
            fetchingRef.current = false;

            return newEdges?.length
              ? {
                  reviews: {
                    __typename: prevResult.reviews.__typename,
                    edges: [...prevResult.reviews.edges, ...newEdges],
                    pageInfo,
                    totalCount,
                  },
                }
              : prevResult;
          },
        });
      }
    });

    if (secondLastItemRef.current) {
      observer.observe(secondLastItemRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [data]);

  const handleTopButtonClick = () => {
    if (modalContentWrapperRef.current) {
      modalContentWrapperRef.current.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  // Modal Open & Close
  const handleSortingFilterModalOpen = () => {
    setIsSortingFilterModalOpen(true);
  };

  const handleSortingFilterModalClose = () => {
    setIsSortingFilterModalOpen(false);
  };

  const handleFilterModalOpen = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setIsFilterModalOpen(false);
  };

  return (
    <Modal id="mobile-total-review-modal-container" open={open} onClose={onClose} onClick={(e) => e.stopPropagation()}>
      <div id="mobile-total-review-modal-content-wrapper" ref={modalContentWrapperRef}>
        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="fixed-area">
              <MobileCustomHeader type="center" isShowBackBtn={true} onBackBtnClick={onClose} pageTitle="Reviews" />

              <div className="total-review-modal-top-wrapper">
                <div className="rating-wrapper">
                  <Rating max={5} value={5} icon={<StarIcon fillColor="#212121" />} readOnly />

                  <Typography cName="variant-text" variant="body3" component="span" style={{ color: '#666666' }}>
                    {`${data?.reviews.totalCount} Reviews`}
                  </Typography>
                </div>

                <div className="sorting-icon-wrapper">
                  <SortListIcon onClick={handleSortingFilterModalOpen} />
                  <FilterListIcon onClick={handleFilterModalOpen} />
                </div>
              </div>
            </div>

            <div className="total-review-item-wrapper">
              {data.reviews.edges.map((edge, index) => (
                <React.Fragment key={edge.node.id}>
                  <MobileReviewListItem review={edge.node} refetch={refetch} showCommentSimple={false} />
                  {index === data.reviews.edges.length - 2 && <div ref={secondLastItemRef} />}
                </React.Fragment>
              ))}
            </div>
          </>
        )}

        {isSortingFilterModalOpen && (
          <MobileReviewSortingFilter
            open={isSortingFilterModalOpen}
            onClose={handleSortingFilterModalClose}
            variables={variables}
            setVariables={setVariables}
          />
        )}

        {isFilterModalOpen && (
          <MobileReviewFilter
            open={isFilterModalOpen}
            onClose={handleFilterModalClose}
            variables={variables}
            setVariables={setVariables}
          />
        )}

        {isShowTopBtn && <TopButton isMobile={true} onClick={handleTopButtonClick} />}
      </div>
    </Modal>
  );
};

export default MobileTotalReviewModal;
